import React, { useState, useRef, useEffect, useCallback } from 'react';
import { List, Input, InputNumber, Button, Tag, Avatar, Tooltip } from 'antd';
import type { InputRef } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useConfirmation } from '../../providers/ConfirmationContext';
import type { Combatant } from '../../types/types';
import styles from './CombatantListItem.module.css';

interface CombatantListItemProps {
  combatant: Combatant;
  isActiveTurn: boolean;
  isNextTurn: boolean;
  setupMode: boolean;
  imageMap: Map<number, string>;
  /** Handler for HP value changes */
  onHpChange: (value: number | null) => void;
  /** Handler for initiative value changes */
  onInitiativeChange: (value: number | null) => void;
  /** Handler for removing this combatant from the encounter */
  onRemoveCombatant: () => void;
  /** Handler for adding a status effect by name */
  onAddStatus: (status: string) => void;
  /** Handler for removing a status effect by name */
  onRemoveStatus: (status: string) => void;
}

/**
 * Individual combatant row in the combat tracker list.
 *
 * Pure presenter component (ADR-034): receives all data and action callbacks
 * as props. ZERO store access. Uses useConfirmation (UI provider hook, not
 * a store hook) for delete confirmation dialog.
 *
 * Uses useState for local UI state (status input visibility) and useSortable
 * for drag-and-drop infrastructure.
 */
const CombatantListItem: React.FC<CombatantListItemProps> = ({
  combatant,
  isActiveTurn,
  isNextTurn,
  setupMode,
  imageMap,
  onHpChange,
  onInitiativeChange,
  onRemoveCombatant,
  onAddStatus,
  onRemoveStatus,
}) => {
  const { confirm } = useConfirmation();
  const [addingStatus, setAddingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const statusInputRef = useRef<InputRef>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(combatant.id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 'auto',
    opacity: isDragging ? 0.8 : 1,
  };

  // Focus the status input when entering add mode.
  // The dependency on addingStatus is correct here - we want this effect
  // to run specifically when the user toggles the add status mode.
  useEffect(() => {
    if (addingStatus) {
      statusInputRef.current?.focus();
    }
  }, [addingStatus]);

  // Wrap the remove handler with a confirmation dialog.
  // useConfirmation is a UI provider hook (not a store hook), so it is
  // allowed in presenters per ADR-034.
  const handleRemoveCombatant = useCallback(() => {
    confirm({
      message: `Remove ${combatant?.name ?? 'this combatant'} from the encounter?`,
      onConfirm: () => onRemoveCombatant(),
    });
  }, [confirm, combatant?.name, onRemoveCombatant]);

  // Using useCallback here since newStatus is local state that changes frequently.
  const handleAddStatus = useCallback(() => {
    if (newStatus.trim()) {
      onAddStatus(newStatus.trim().toLowerCase());
      setNewStatus('');
      setAddingStatus(false);
    } else {
      setAddingStatus(false);
    }
  }, [newStatus, onAddStatus]);

  // Defensive check: if combatant is undefined or missing critical properties,
  // return null to prevent runtime errors during state transitions or hydration.
  // Placed after all hooks to satisfy React's rules-of-hooks requirement.
  if (!combatant || combatant.id === undefined || combatant.id === null) {
    return null;
  }

  const classNames = [
    styles.item,
    isActiveTurn ? styles.active : '',
    isNextTurn ? styles.nextTurn : '',
    (combatant.hp ?? 0) <= 0 ? styles.downed : '',
  ].filter(Boolean).join(' ');

  const combatantImage = combatant.imageId ? imageMap.get(combatant.imageId) : null;

  return (
    <List.Item
      ref={setNodeRef}
      style={style}
      className={classNames}
      actions={
        setupMode
          ? [
              <Button
                key="remove"
                icon={<DeleteOutlined />}
                danger
                size="small"
                onClick={handleRemoveCombatant}
                title={`Remove ${combatant?.name ?? 'combatant'}`}
              />,
            ]
          : []
      }
    >
      <div className={styles.dragHandle} {...listeners} {...attributes}>
        ☰
      </div>
      <List.Item.Meta
        avatar={
          <Avatar shape="square"
            src={combatantImage}
            style={{ backgroundColor: combatant?.color ?? '#ccc' }}
          >
            {combatant?.name?.charAt(0) ?? '?'}
          </Avatar>
        }
        title={
          <div className={styles.nameWrapper}>
            {(combatant?.hp ?? 0) <= 0 && <span className={styles.downedIcon} title="Downed/Unconscious">💀</span>}
            {combatant?.name ?? 'Unknown'}
          </div>
        }
        description={
          <div className={styles.statuses}>
            {combatant.statusEffects?.map((status: string) => (
              <Tag
                key={status}
                closable
                onClose={() => onRemoveStatus(status)}
                color="blue"
              >
                {status}
              </Tag>
            ))}
            {addingStatus ? (
              <Input
                ref={statusInputRef}
                size="small"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                onPressEnter={handleAddStatus}
                onBlur={handleAddStatus}                
              />
            ) : (
              <Button size="small" icon={<PlusOutlined />} onClick={() => setAddingStatus(true)}>
                Status
              </Button>
            )}
          </div>
        }
      />
      <div className={styles.stats}>
        {setupMode ? (
          <InputNumber value={combatant.initiative} onChange={onInitiativeChange} size="small" style={{ width: 50 }} placeholder="Init" />
        ) : (
          <Tooltip title="Initiative">{combatant.initiative}</Tooltip>
        )}
        <InputNumber value={combatant.hp ?? 0} onChange={onHpChange} size="small" className={styles.hpInput} min={0} max={combatant.maxHp ?? undefined} />
        <span className={styles.maxHp}> / {combatant.maxHp}</span>
      </div>
    </List.Item>
  );
};

export default CombatantListItem;